<script setup lang="ts">
import { computed, inject } from "vue"
import { useRoute } from "vue-router"
import { useQuery } from "@tanstack/vue-query"
import { ChevronRight, Download } from "lucide-vue-next"
import { useI18n } from "vue-i18n"

import { doLogin } from "@/lib/router_utils"
import { getLocalizedAbstract, listAuthors } from "@/lib/article.ts"
import { doGetRequest, generateDownloadLink } from "@/lib/utils.ts"

import { getArticle, getArticleAttachments } from "@/models/article"
import { CurrentUser, getPermissions } from "@/lib/user.ts"
import { LoaderCircle } from "lucide-vue-next"

const currentUser = inject("currentUser") as CurrentUser
const permissions = getPermissions("studimusicali")

import Button from "@/components/ui/button/Button.vue"
import Separator from "@/components/ui/separator/Separator.vue"
import AttachmentSidebar from "@/components/ui-custom/attachment-sidebar/AttachmentSidebar.vue"
import Navbar from "@/components/ui-custom/navbar/Navbar.vue"

const route = useRoute()
const articleId = route.params.id as string

const { t } = useI18n()

const { data: article } = useQuery({
  queryKey: ["articles", route.params.id],
  queryFn: ({ signal }) => getArticle({ signal: signal, id: articleId }),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const {
  data: attachments,
  isLoading,
  isError
} = useQuery({
  queryKey: ["articles", route.params.id, "attachments"],
  queryFn: ({ signal }) =>
    getArticleAttachments({
      signal: signal,
      documentId: documentId.value,
      articleId: articleId,
      publicationId: article?.value?.publication_id
    }),

  staleTime: 10 * 60 * 1000, // 10 minutes
  enabled: computed(() => !!article.value && !!currentUser.user)
})

const selectedFile = computed(() => {
  return {
    ...article.value,
    id: documentId.value
  }
})

const documentId = computed(() => {
  return article?.value?.document_id
})

const downloadUrl = computed(() => {
  if (!documentId.value) return ""
  if (!article?.value?.blob_id) return ""

  let filename = `${t("commons.publication")}_`
  filename += `${article.value?.publication_year}_${article.value?.publication_number}_`
  filename += `${t("commons.article_page").toLocaleLowerCase()}_`
  filename += `${article.value?.start_page}-${article.value?.end_page}.pdf`

  return generateDownloadLink(documentId.value, article?.value?.blob_id, filename)
})

const updateStats = (): void => {
  doGetRequest("/api/update_stats/download", {
    params: {
      type: "article",
      article_id: articleId,
      publication_id: article?.value?.publication_id
    }
  })
}

const handleDownload = (): void => {
  updateStats()
}

const reloadPage = () => {
  window.location.reload()
}
</script>

<template>
  <navbar />

  <div class="container flex flex-col lg:flex-row" v-if="article">
    <div class="flex flex-1 flex-col gap-4 pr-4">
      <div class="title">{{ $t("articles.title") }}</div>
      <div class="text-2xl">{{ article.title }}</div>

      <Separator />

      <div class="flex justify-between" v-if="article.authors">
        <div class="text-primary">{{ $t("articles.author") }}:</div>
        <div>{{ listAuthors(article.authors) }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("articles.language") }}:</div>
        <div>{{ article.language ? $t(`commons.localized_languages.${article.language}`) : "..." }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("commons.year") }}:</div>
        <div>{{ article.publication_year }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("commons.number") }}:</div>
        <div>{{ article.publication_number }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("articles.pages") }}:</div>
        <div>{{ article.start_page }}-{{ article.end_page }}</div>
      </div>

      <Separator />

      <router-link :to="{ name: 'show_publication_path', params: { id: article.publication_id } }">
        <div class="flex-0 flex text-primary hover:text-foreground">
          <div class="flex-1">{{ $t("articles.show_link") }}</div>
          <ChevronRight />
        </div>
      </router-link>

      <template v-if="currentUser.user && permissions.includes('download')">
        <Separator />

        <div class="flex-0 flex cursor-pointer text-primary hover:text-foreground">
          <a :href="downloadUrl" @click="handleDownload" class="flex w-full flex-row">
            <span class="flex-1">Download PDF</span>

            <span class="ml-auto">
              <Download />
            </span>
          </a>
        </div>
      </template>

      <Separator />

      <div class="flex flex-col gap-2" v-if="getLocalizedAbstract(article)">
        <div class="text-xl">{{ $t("articles.summary") }}:</div>
        <div>{{ getLocalizedAbstract(article) }}</div>
      </div>
    </div>

    <div class="flex max-w-[500px] flex-1 flex-col p-4">
      <div class="relative flex min-h-[740px] border border-gray-400">
        <div class="flex h-full w-full flex-col items-center justify-center" v-if="isLoading">
          <LoaderCircle class="h-20 animate-spin" />
        </div>

        <template v-else-if="currentUser.user && permissions.includes('read') && !isError">
          <attachment-sidebar :slides="attachments" :selected-file="selectedFile" />
        </template>

        <div class="flex h-full w-full flex-col items-center justify-center gap-2" v-else-if="isError">
          <div>Si è verificato un errore durante il caricamento.</div>
          <Button type="button" @click="reloadPage">Ricarica</Button>
        </div>

        <div class="w-full" v-else>
          <div class="flex h-full flex-1 flex-col items-center justify-center gap-4">
            <template v-if="!currentUser.user || isError">
              <div>{{ $t("commons.unauthorized") }}</div>

              <Button @click="doLogin()">{{ $t("commons.login") }}</Button>
            </template>

            <div v-else>
              {{ $t("commons.missing_permissions") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pswp {
  z-index: 10;
}
</style>
